// Markup step — used by purchased parts (step 2 of 3).
//
// Captures the markup percentage applied to the vendor cost (default 20%),
// optional declared-value adjustment, and notes. Maps to
// `part.purchasedSource.markupPct` per crmTypes.

import { NumberInput } from '@/components/NumberInput';
import type { StepProps } from '../stepTypes';
import type { PurchasedSource } from '@/shared/crmTypes';

const DEFAULT_MARKUP = 20;

export function MarkupStep({ part, onChange }: StepProps) {
  const ps: PurchasedSource = part.purchasedSource ?? { vendor: 'other' };

  function updateSource(patch: Partial<PurchasedSource>) {
    onChange({ purchasedSource: { ...ps, ...patch } });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Markup %">
          <div className="flex items-center gap-2">
            <div className="w-32">
              <NumberInput
                value={ps.markupPct}
                onChange={(v) => updateSource({ markupPct: v })}
                step={1}
                unit="%"
                placeholder={String(DEFAULT_MARKUP)}
                numpadTitle="Markup %"
              />
            </div>
            <span className="text-xs text-text-mute">
              default <span className="font-mono">{DEFAULT_MARKUP}%</span>
            </span>
          </div>
        </Field>
        <Field label="Declared value adj. ($)">
          <NumberInput
            value={part.setupCost}
            onChange={(v) => onChange({ setupCost: v })}
            step={1}
            unit="$"
            placeholder="0"
            numpadTitle="Declared value adjustment"
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          value={part.notes ?? ''}
          onChange={(e) => onChange({ notes: e.target.value })}
          rows={3}
          placeholder="Anything quote-relevant about this component"
          className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange resize-y"
        />
      </Field>

      <div className="text-xs text-text-mute italic">
        Real cost roll-up (vendor cost × markup × qty) lives in the Quote builder's
        pricing engine — this step just captures the markup factor for that calc.
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
